import { defineStore, storeToRefs } from 'pinia';
import { ref, computed } from 'vue';
import router from '@/router';
import { format } from 'date-fns';
import useHttp from '@/utils/httpClient';
import { useAppConfigStore } from '@/stores/appConfig.store';
import { useBookingQueryStore } from '@stores/bookingQuery.store';
import { useReservationStore } from '@stores/reservation.store';
import { useAddonsStore } from '@stores/addons.store';
import { useOffersStore } from '@stores/offers.store';
import { useUserProfile } from '@stores/userProfile.store';

import { useMobileDevices } from '@composables/useScreenSize';
const { isMobile } = useMobileDevices();

export const usePaymentSummaryStore = defineStore('paymentSummary', () => {
  const appConfigStore = useAppConfigStore();
  const { applicationSetup } = storeToRefs(appConfigStore);

  const bookingQueryStore = useBookingQueryStore();
  const { activatedPromotionalCodes, query } = storeToRefs(bookingQueryStore);

  const reservationStore = useReservationStore();
  const { reservations } = storeToRefs(reservationStore);

  const addonsStore = useAddonsStore();
  const { addons: storeAddons } = storeToRefs(addonsStore);

  const http = useHttp();
  const paymentSummaryDetails = ref({});
  const summaryParams = ref({});
  const hotelId = applicationSetup.value.hotel_id;

  const offersStore = useOffersStore();
  const { currentRoom } = storeToRefs(offersStore);

  const userProfileStore = useUserProfile();
  const { userProfile } = storeToRefs(userProfileStore);

  const loading = ref(false);

  // Add price change tracking state
  const hasPriceChangeBeenShown = ref(false);
  const showPriceChangeModal = ref(false);
  const priceInfo = ref({ hasChange: false, isIncreased: false });

  const getPaymentSummary = async () => {
    if (!applicationSetup.value.currency) {
      return;
    }

    let occupancies = [];
    for (let i = 0; i < Object.keys(reservations.value).length; i++) {
      let occupancy = JSON.parse(
        JSON.stringify(reservations.value[i].occupancy),
      );
      occupancy.addons = reservations.value[i].addons;
      occupancy.meal_plan_id = reservations.value[i].mealPlan.meal_plan_id;
      occupancies.push(occupancy);
    }

    // Format hotel_addons before sending
    const formattedAddons = storeAddons.value.map((addon) => ({
      addon_id: addon.id,
      quantity: addon.quantity,
    }));

    const requestParams = {
      language: applicationSetup.value.language,
      checkin: format(reservations.value[0].checkin, 'yyyy-MM-dd'),
      checkout: format(reservations.value[0].checkout, 'yyyy-MM-dd'),
      currency: applicationSetup.value.currency,
      hotel_addons: formattedAddons,
      occupancies: occupancies,
      is_rezlock: reservations.value[0].rezlock ?? false,
      is_mobile: isMobile.value,
      login: userProfile.value.email,
    };

    if (query.value.filterOffers) {
      requestParams.filter_offers = query.value.filterOffers;
    }

    doRequest(requestParams);
  };

  const getSummaryOnDemand = async (roomRateCode, mealPlanId) => {
    let occupancy = query.value.occupancy[currentRoom.value];
    let formattedAddons = [];
    if (storeAddons.value.length > 0) {
      formattedAddons = storeAddons.value.map((addon) => ({
        room_rate_code: roomRateCode,
        addon_id: addon.id,
        quantity: addon.quantity,
      }));
    }

    const requestParams = {
      language: applicationSetup.value.language,
      checkin: format(query.value.startDate, 'yyyy-MM-dd'),
      checkout: format(query.value.endDate, 'yyyy-MM-dd'),
      currency: applicationSetup.value.currency,
      is_mobile: isMobile.value,
      occupancies: [
        {
          room_rate_code: roomRateCode,
          adults: occupancy.nrAdults,
          children: occupancy.nrChildren,
          babies: occupancy.nrBabies,
          addons: formattedAddons,
          meal_plan_id: mealPlanId ?? 1,
          children_ages: occupancy.children_ages ? occupancy.children_ages : [],
        },
      ],
      login: userProfile.value.email,
    };

    if (query.value.filterOffers) {
      requestParams.filter_offers = query.value.filterOffers;
    }

    doRequest(requestParams);
  };

  const checkPriceChange = (roomRateCode) => {
    if (hasPriceChangeBeenShown.value) return;

    const summaryPrice =
      paymentSummaryDetails.value?.summary?.occupancies?.[0]?.room_rate
        ?.original_net_price;
    if (!summaryPrice) return;

    const roomRateInfo = offersStore.getRoomRateInfo(
      ...roomRateCode.split('.'),
    );

    if (!roomRateInfo?.original_net_price) return;

    const hasChange = summaryPrice !== roomRateInfo.original_net_price;
    if (hasChange) {
      showPriceChangeModal.value = true;
      hasPriceChangeBeenShown.value = true;
      priceInfo.value = {
        hasChange,
        isIncreased: summaryPrice > roomRateInfo.original_net_price,
      };
      offersStore.getOffers(true);
    }
  };

  const resetPriceChangeState = () => {
    hasPriceChangeBeenShown.value = false;
    showPriceChangeModal.value = false;
    priceInfo.value = { hasChange: false, isIncreased: false };
  };

  const doRequest = async (requestParams) => {
    const { campaignCode, promotionCodes } = computed(() => {
      return activatedPromotionalCodes.value.reduce(
        (acc, code) => {
          if (code.isCampaignCode) {
            acc.campaignCode = code.value;
          } else {
            acc.promotionCodes.push(code.value);
          }
          return acc;
        },
        { campaignCode: '', promotionCodes: [] },
      );
    }).value;

    if (campaignCode) {
      requestParams.campaign_code = campaignCode;
    }

    if (promotionCodes.length > 0) {
      requestParams.promotion_code = promotionCodes;
    }
    summaryParams.value = requestParams;
    loading.value = true;
    const response = await http.get(
      `${hotelId}/payment/summary`,
      requestParams,
    );
    loading.value = false;
    paymentSummaryDetails.value = response.data;

    // Check for price changes after summary update
    if (requestParams.occupancies?.[0]?.room_rate_code) {
      checkPriceChange(requestParams.occupancies[0].room_rate_code);
    }
  };

  const getMbwayStatus = async () => {
    try {
      const transId = router.currentRoute.value.query.transId;
      const gcTransId = router.currentRoute.value.query.id;

      const requestParams = {
        trans_id: transId,
        gc_trans_id: gcTransId,
      };

      const response = await http.get(
        `${hotelId}/payment/status`,
        requestParams,
      );

      return response.data;
    } catch (error) {
      console.error('Error fetching payment summary:', error);
      throw error;
    }
  };

  return {
    loading,
    summaryParams,
    getPaymentSummary,
    paymentSummaryDetails,
    getSummaryOnDemand,
    getMbwayStatus,
    showPriceChangeModal,
    priceInfo,
    resetPriceChangeState,
  };
});
